import { ApiError } from "@/types/api";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, query, updateDoc, where, writeBatch } from "firebase/firestore";
import { db } from "@/firebase";
import { Drivers } from "@/types/drivers";
import { pick } from "lodash";

export const driversApi = createApi({
    reducerPath: "drivers",
    baseQuery: fakeBaseQuery<ApiError>(),
    tagTypes: ["Drivers", "Organization"],
    endpoints: (builder) => ({
        fetchDrivers: builder.query<Drivers[], string>({
            queryFn: async (organizationId: string) => {
                try {
                    if (!organizationId) {
                        return {
                            error: { status: 400, statusText: "Faltan parámetros" }
                        }
                    }

                    const collectionRef = collection(db, "drivers")
                    const q = query(
                        collectionRef,
                        where("organizations", "array-contains", organizationId)
                    );

                    const querySnapshot = await getDocs(q);

                    const drivers = querySnapshot.docs.map((doc) => ({
                        ...doc.data(),
                        id: doc.id,
                    })) as Drivers[];

                    return { data: drivers };
                } catch (error) {
                    return {
                        error: { status: 500, statusText: "Error al obtener los repartidores" }
                    }
                }
            },
            providesTags: ['Drivers'],
        }),
        createDriver: builder.mutation<Drivers, Drivers>({
            queryFn: async (payload: Drivers, { getState }) => {
                try {

                    const state = getState() as any;
                    const user = state.auth?.user;

                    const newDriver: Drivers = {
                        ...payload,
                        organizations: [...(payload.organizations ?? [])],
                        createdAt: Date.now(),
                        createdBy: user.uid,
                    }

                    const docRef = await addDoc(collection(db, "drivers"), newDriver);
                    return {
                        data: {
                            ...newDriver,
                            id: docRef.id,
                        }
                    };

                } catch (error) {
                    return {
                        error: { status: 500, statusText: "Error al crear el repartidor" }
                    }
                }
            },
            invalidatesTags: ['Drivers'],
        }),
        updateDriver: builder.mutation<any, { draft: Partial<Drivers> & { id: string } }>({
            queryFn: async ({ draft }, { getState }) => {
                try {
                    const store = getState() as any;

                    // ✅ VALIDACIÓN CORRECTA
                    if (!draft?.id) {
                        return {
                            error: {
                                status: 400,
                                statusText: "Falta el ID",
                            },
                        };
                    }

                    const driverRef = doc(db, `drivers/${draft.id}`);
                    const driverSnap = await getDoc(driverRef);

                    if (!driverSnap.exists()) {
                        return {
                            error: {
                                status: 404,
                                statusText: "El repartidor no existe",
                            },
                        };
                    }

                    const ALLOWED_FIELDS = ["name", "phone"];

                    const payload: Partial<Drivers> = {
                        ...pick(draft, ALLOWED_FIELDS),
                        lastUpdatedBy: store?.auth?.user?.uid,
                        lastUpdatedAt: Date.now(),
                    };

                    await updateDoc(driverRef, payload);

                    return {
                        data: {
                            id: draft.id,
                            ...driverSnap.data(),
                            ...payload,
                        },
                    };

                } catch (error) {
                    return {
                        error: {
                            status: 500,
                            statusText: "Error al actualizar al repartidor"
                        }
                    }
                }
            },
            invalidatesTags: ['Drivers'],
        }),
        //
        deleteDriver: builder.mutation<any, string>({
            queryFn: async (driverId: string) => {
                try {
                    const driverRef = doc(db, `drivers/${driverId}`);
                    await deleteDoc(driverRef);
                    return { data: driverId };
                } catch (error) {
                    return {
                        error: {
                            status: 500,
                            statusText: "Error al eliminar al repartidor"
                        }
                    }
                }
            },
            invalidatesTags: ['Drivers'],
        }),
        updateOrganizationDriversBatch: builder.mutation<void, { organizationId: string, driversFromForm: any[] }>({
            queryFn: async ({ organizationId, driversFromForm }, { getState }) => {
                try {
                    const state = getState() as any;
                    const userId = state.auth?.user?.uid;

                    if (!userId) throw new Error("Usuario no autenticado");

                    const batch = writeBatch(db);
                    const driverIdsForSetting: string[] = [];
                    const now = Date.now();

                    for (const driver of driversFromForm) {
                        let driverRef;

                        if (driver.isNew) {
                            driverRef = doc(collection(db, "drivers"));
                            batch.set(driverRef, {
                                name: driver.name,
                                phone: String(driver.phone || ""),
                                organizations: [organizationId],
                                createdAt: now,
                                createdBy: userId,
                            });
                            driverIdsForSetting.push(driverRef.id);
                        } else if (driver.isUpdated) {
                            driverRef = doc(db, "drivers", driver.id);
                            batch.update(driverRef, {
                                name: driver.name,
                                phone: String(driver.phone || ""),
                                lastUpdatedBy: userId,
                                lastUpdatedAt: now,
                            });
                            driverIdsForSetting.push(driverRef.id);
                        } else if (driver.isDeleted) {
                            driverRef = doc(db, "drivers", driver.id);
                            batch.delete(driverRef);
                        } else {
                            driverIdsForSetting.push(driver.id);
                        }
                    }

                    await batch.commit();
                    return { data: { driverIds: driverIdsForSetting } };
                } catch (error: any) {
                    console.error("Fallo en Firebase:", error);
                    return { error: { status: 500, statusText: error.message } };
                }
            },
            invalidatesTags: ["Drivers"],
        }),

    })
});

export const { useFetchDriversQuery, useCreateDriverMutation, useUpdateDriverMutation, useDeleteDriverMutation, useUpdateOrganizationDriversBatchMutation } = driversApi;